import {
  saveAccountData,
  getSubscriptionOptions,
  addDeveloperIP,
  changeURLHijacking,
  removeDeveloperIP,
  changeSubscription,
  closeAccount,
  changeCreditCard,
  changePassword,
  resetKeys,
  changeSummary,
  forgotPassword,
  resetPasswordWithToken,
  registerUser,
  getData,
} from "../../api/api";

const state = {
  error: false,
  accountData: {
    userDto: { firstName: "", lastName: "" },
  },
  subscriptionOptions: [],
  hideArchiveModal: false,
  timerSortSelected: "End Date (Oldest to Newest)",
};

// getters
const getters = {
  // get all account data
  data: state => {
    return state.accountData;
  },
  paymentReceipts: state => {
    return state.accountData.paymentReceipts;
  },
  paymentReceipt: state => id => {
    return state.accountData.paymentReceipts.find(s => s.id === id);
  },
  email: state => {
    return state.accountData.userDto.credentials_Email;
  },
  customer: state => {
    return state.accountData.customer;
  },
  apiKey: state => {
    return state.accountData.accountDto.apiKey;
  },
  secretKey: state => {
    return state.accountData.accountDto.secret;
  },
  defaultCard: state => {
    return state.accountData.defaultCard;
  },
  user: state => {
    return state.accountData.userDto;
  },
  subscription: state => {
    return state.accountData.subscriptionDto;
  },
  subscriptionOption: state => type => {
    return state.subscriptionOptions.find(s => s.subscriptionType === type);
  },
  subscriptionOptions: state => {
    return state.subscriptionOptions;
  },
  sortedSubscriptionOptions: state => {
    const options = [...state.subscriptionOptions];
    return options.sort((a, b) => {
      if (a.levelOrder > b.levelOrder) {
        return 1;
      }
      return -1;
    });
  },
  averageImpressions: state => {
    return state.accountData.averageImpressionPerMonth;
  },
  developerIps: state => {
    return state.accountData.developerIps;
  },
  account: state => {
    return state.accountData.accountDto;
  },
  currentIP: state => {
    return state.accountData.currentIP;
  },
  urlHijacking: state => {
    return state.accountData.accountDto.allowUrlHijacking;
  },
  hideArchiveModal: state => {
    return state.hideArchiveModal;
  },
  timerSortSelected: state => {
    return state.timerSortSelected;
  },
  accountStatus: (state, getters) => {
    if (!getters.account.active) return "Inactive";
    if (getters.account.suspended) return "Suspended";
    return 'Active';
  },
  isAccountActive: (state, getters) => {
    return getters.accountStatus === "Active";
  },
};

// actions
const actions = {
  getAllData({ rootState, dispatch }) {
    const token = rootState.session.token;
    return getData(token).then(result => {
      dispatch("session/setData", result, { root: true });
    });
  },
  // create new user
  createUser({}, payload) {
    return registerUser(payload);
  },
  // resets API keys
  resetAPIKeys({ dispatch, rootState }) {
    const token = rootState.session.token;
    return resetKeys(token).then(result => {
      dispatch("session/setData", result, { root: true });
    });
  },
  // gets available subscription options from API
  getSubscriptionOptions({ commit }) {
    return getSubscriptionOptions()
      .then(result => {
        commit("setSubscriptionOptions", result.data);
      })
      .catch(error => {
        console.log("error", error);
      });
  },
  // gets summary of changes when user chooses a new account or billing period
  getChangeSummary({ rootState }, payload) {
    const token = rootState.session.token;
    return changeSummary(payload, token).then(result => {
      return result.data;
    });
  },
  updateURLHijacking({ dispatch, rootState }, payload) {
    const token = rootState.session.token;
    return changeURLHijacking(payload, token).then(result => {
      dispatch("session/setData", result, { root: true });
      // commit("setURLHijacking", payload);
    });
  },
  // takes a field name and value and saves that data
  setFormValue({ commit }, payload) {
    commit("setFormValue", payload);
  },
  /**
   * save account data via API
   * saveFields is an array of objects with this format:
   * { field: "nameOfField", section: "optionalSectionName", value: "newValue" }
   */
  saveData({ commit, state, rootState }, saveFields) {
    const token = rootState.session.token;
    const newData = JSON.parse(JSON.stringify(state.accountData));
    for (const field of saveFields) {
      if (field.section) {
        newData[field.section][field.field] = field.value;
      } else {
        newData[field.field] = field.value;
      }
    }
    return saveAccountData(newData, token).then(result => {
      // save account data sent back from api
      commit("setData", result.data.account);
      // save new token
      commit("session/setToken", result.data.dashboardToken, {
        root: true,
      });
    });
  },
  saveAllData({ commit, state, rootState }) {
    return saveAccountData(state.accountData, rootState.session.token)
      .then(result => {
        // save account data sent back from api
        commit("setData", result.data.account);
        // save new token
        commit("session/setToken", result.data.dashboardToken, {
          root: true,
        });
      })
      .catch(error => {
        console.log("error", error);
        commit("errorTrue");
      });
  },
  saveEmail({ commit, dispatch }, newEmail) {
    commit("setValue", {
      field: "credentials_Email",
      value: newEmail,
      section: "userDto",
    });
    commit("setFormValue", {
      field: "credentials_Email",
      value: newEmail,
      section: "userDto",
    });
    return dispatch();
  },
  savePassword({ dispatch, rootState }, payload) {
    const token = rootState.session.token;
    return changePassword(payload, token).then(result => {
      return dispatch("session/setToken", result.data.dashboardToken, {
        root: true,
      });
    });
  },
  resetPassword({}, email) {
    return forgotPassword(email);
  },
  changePasswordWithToken({}, payload) {
    return resetPasswordWithToken(payload.newPassword, payload.token);
  },
  addIPAddress({ dispatch, rootState }, newIP) {
    const token = rootState.session.token;
    const newAddress = {
      ipAddress: newIP.ipAddress,
      name: newIP.name,
    };
    // add developer IP and store the resulting data
    return addDeveloperIP(newAddress, token).then(result => {
      dispatch("session/setData", result, { root: true });
    });
  },
  removeIPAddress({ dispatch, rootState }, payload) {
    const token = rootState.session.token;
    // add developer IP and store the resulting data
    return removeDeveloperIP(payload, token).then(result => {
      dispatch("session/setData", result, { root: true });
    });
  },
  changeSubscription({ rootState, dispatch }, payload) {
    const newSubscription = {
      subscriptionType: payload.subscriptionType,
      billingCycle: payload.billingCycle,
    };
    const token = rootState.session.token;
    return changeSubscription(newSubscription, token).then(result => {
      dispatch("session/setData", result, { root: true });
    });
  },
  closeUserAccount({ rootState, dispatch }) {
    const token = rootState.session.token;
    return closeAccount(token)
  },
  updateCreditCard({ rootState, dispatch }, payload) {
    const token = rootState.session.token;
    console.log(payload);
    return changeCreditCard(payload, token).then(result => {
      dispatch("session/setData", result, { root: true });
    });
  },
};

// mutations
const mutations = {
  // sets all the fields in the original account data
  setData(state, payload) {
    state.accountData = payload;
  },
  setSubscriptionOptions(state, payload) {
    state.subscriptionOptions = payload;
  },
  setValue(state, payload) {
    if (payload.section) {
      state.accountData[payload.section][payload.field] = payload.value;
    } else {
      state.accountData[payload.field] = payload.value;
    }
  },
  addIPAddress(state, payload) {
    state.accountData.developerIps.push(payload);
  },
  removeIPAddress(state, payload) {
    const index = state.accountData.developerIps.find(i => i.id === payload.id);
    state.accountData.developerIps.splice(index, 1);
  },
  errorTrue(state) {
    state.error = true;
  },
  errorFalse(state) {
    state.error = false;
  },
  setHideArchiveModal(state, payload) {
    state.hideArchiveModal = payload;
  },
  setTimerSortSelected(state, payload) {
    state.timerSortSelected = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
